import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { brand, neutral } from "../../theme/palette";
import { formatINR } from "../../utils/formatINR";
import { formatDecimal, formatNumber, vehicleUsage } from "../../utils/analyticsMetrics";
import { ChartCard, ChartTooltip, Skeleton, TableSkeleton, axisProps } from "./AnalyticsUI";

const lastChargedFmt = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" });

const Stat = ({ label, value, hint, isLoading }) => (
  <div>
    <p className="text-xs font-medium text-muted">{label}</p>
    {isLoading ? <Skeleton className="mt-1.5 h-6 w-20" /> : <p className="mt-0.5 text-xl font-semibold tabular-nums text-fg">{value}</p>}
    {hint && <p className="text-xs text-muted">{hint}</p>}
  </div>
);

const VehicleAnalytics = ({ sessions, isLoading, error }) => {
  const usage = useMemo(() => vehicleUsage(sessions), [sessions]);
  const top = useMemo(() => [...usage.vehicles].sort((a, b) => b.sessions - a.sessions || b.energy - a.energy).slice(0, 8), [usage.vehicles]);
  const empty = !usage.total;

  return (
    <div className="space-y-4">
      <div className="card grid grid-cols-2 gap-6 p-5 md:grid-cols-4">
        <Stat label="Distinct vehicles" value={formatNumber(usage.total)} isLoading={isLoading} />
        <Stat label="Avg sessions per vehicle" value={formatDecimal(usage.avgSessionsPerVehicle)} isLoading={isLoading} />
        <Stat
          label="Returning vehicles"
          value={`${(usage.repeatShare * 100).toFixed(1)}%`}
          hint={`${formatNumber(usage.repeat)} charged 2+ times`}
          isLoading={isLoading}
        />
        <Stat label="Energy consumed" value={`${formatDecimal(usage.energy)} kWh`} isLoading={isLoading} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <ChartCard
          className="lg:col-span-2"
          title="How often vehicles charge"
          description="Vehicles grouped by number of sessions in the period."
          isLoading={isLoading}
          error={error}
          isEmpty={empty}
          height={240}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={usage.bands} margin={{ top: 16, right: 4, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={neutral[200]} strokeDasharray="3 3" />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} width={36} allowDecimals={false} />
              <Tooltip cursor={{ fill: neutral[100] }} content={<ChartTooltip formatters={{ vehicles: formatNumber }} labelFormatter={(l) => `${l} in period`} />} />
              <Bar dataKey="vehicles" name="Vehicles" fill={brand[600]} radius={[3, 3, 0, 0]} maxBarSize={44} label={{ position: "top", fontSize: 11, fill: neutral[600] }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="card overflow-hidden lg:col-span-3">
          <div className="border-b border-line px-5 py-4">
            <h3 className="section-title">Most active vehicles</h3>
            <p className="mt-0.5 text-13 text-muted">By number of charging sessions in the period.</p>
          </div>
          {isLoading ? (
            <div className="px-5 py-3"><TableSkeleton rows={6} /></div>
          ) : error ? (
            <p className="px-5 py-10 text-center text-13 text-red-700">Couldn't load vehicle data. {error}</p>
          ) : empty ? (
            <p className="px-5 py-10 text-center text-13 text-muted">No vehicles charged in the selected period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-13">
                <thead className="border-b border-line">
                  <tr>
                    <th className="h-10 px-4 text-left uppercase">Vehicle</th>
                    <th className="h-10 px-4 text-right uppercase">Sessions</th>
                    <th className="h-10 px-4 text-right uppercase">Energy (kWh)</th>
                    <th className="h-10 px-4 text-right uppercase">Spend</th>
                    <th className="h-10 px-4 text-right uppercase">Stations</th>
                    <th className="h-10 px-4 text-right uppercase">Last charged</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/70">
                  {top.map((v) => (
                    <tr key={v.vehicleId} className="odd:bg-surface even:bg-neutral-100">
                      <td className="px-4 py-2.5 font-medium text-fg">{v.vehicleId}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">{formatNumber(v.sessions)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">{formatDecimal(v.energy)}</td>
                      <td className="px-4 py-2.5 text-right font-medium tabular-nums text-fg">{formatINR(v.spend)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">{formatNumber(v.stations)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-muted">
                        {v.lastCharged ? lastChargedFmt.format(new Date(`${v.lastCharged.slice(0, 10)}T00:00:00Z`)) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleAnalytics;
